import type {
  AffordabilityScenariosRender,
  PaymentSnapshot,
  ScenarioRenderState,
} from "../../../lib/agent-types";
import { formatCurrencyFromCents } from "../../../lib/financials";
import { cn } from "../../../lib/utils";
import { WidgetFrame } from "../widget-frame";

type Row = {
  key: string;
  label: string;
  value: (snap: PaymentSnapshot) => number | null;
  emphasize?: boolean;
  blankWhenZero?: boolean;
};

const ROWS: Row[] = [
  { key: "pi", label: "Principal & interest", value: (s) => s.principal_interest },
  { key: "tax", label: "Property taxes", value: (s) => s.property_taxes },
  { key: "ins", label: "Insurance", value: (s) => s.insurance },
  { key: "pmi", label: "PMI", value: (s) => s.mortgage_insurance, blankWhenZero: true },
  { key: "piti", label: "Total PITI", value: (s) => s.total, emphasize: true },
];

export function AffordabilityScenariosWidget({
  payload,
}: {
  payload: AffordabilityScenariosRender;
}) {
  const scenarios = payload.scenarios;
  const labels = scenarios.map((s) => s.label);
  const snaps = scenarios.map(scenarioSnapshot);
  const baselineTotal = snaps[0]?.total ?? null;

  return (
    <WidgetFrame data-widget-type="affordability_scenarios">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[420px] border-collapse text-sm">
          <thead>
            <tr>
              <th className="sticky left-0 bg-card text-left text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground" />
              {scenarios.map((s) => (
                <th
                  key={s.label}
                  data-scenario={s.label}
                  className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                >
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <StaticRow
              label="Down payment"
              labels={labels}
              values={scenarios.map((scenario, i) =>
                scenarioDownPaymentAmount(scenario, snaps[i]),
              )}
            />
            <StaticRow
              label="Loan amount"
              labels={labels}
              values={snaps.map((s) => s?.loan_amount ?? null)}
            />
            {ROWS.map((row) => (
              <BodyRow key={row.key} row={row} snaps={snaps} labels={labels} />
            ))}
            <tr className="border-t border-border-soft text-xs">
              <th
                scope="row"
                className="sticky left-0 bg-card py-2 pr-3 text-left font-medium text-muted-foreground"
              >
                vs. {scenarios[0]?.label ?? "first"}
              </th>
              {snaps.map((s, i) => {
                const total = s?.total;
                const delta =
                  baselineTotal !== null &&
                  typeof total === "number" &&
                  typeof baselineTotal === "number"
                    ? total - baselineTotal
                    : null;
                return (
                  <td
                    key={labels[i]}
                    data-delta-for={scenarios[i]?.label}
                    className={cn(
                      "px-3 py-2 text-right font-bold tabular-nums",
                      i === 0
                        ? "text-muted-foreground"
                        : delta !== null && delta < 0
                          ? "text-primary-dark"
                          : "text-foreground",
                    )}
                  >
                    {i === 0 || delta === null
                      ? "—"
                      : `${delta < 0 ? "−" : "+"}${formatCurrencyFromCents(Math.abs(delta))}/mo`}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </WidgetFrame>
  );
}

function BodyRow({
  row,
  snaps,
  labels,
}: {
  row: Row;
  snaps: Array<PaymentSnapshot | null>;
  labels: string[];
}) {
  return (
    <tr
      data-row={row.key}
      className={cn(row.emphasize && "border-t border-border-soft")}
    >
      <th
        scope="row"
        className={cn(
          "sticky left-0 bg-card py-2 pr-3 text-left",
          row.emphasize
            ? "text-sm font-semibold text-foreground"
            : "text-sm text-muted-foreground",
        )}
      >
        {row.label}
      </th>
      {snaps.map((s, i) => {
        const raw = s ? row.value(s) : null;
        const isBlankZero = row.blankWhenZero && raw === 0;
        return (
          <td
            key={labels[i]}
            data-col={i}
            data-row-key={row.key}
            className={cn(
              "px-3 py-2 text-right tabular-nums",
              row.emphasize ? "font-semibold text-foreground" : "font-bold text-foreground",
              isBlankZero && "text-muted-foreground",
            )}
          >
            {isBlankZero ? "None" : formatCurrencyFromCents(raw)}
          </td>
        );
      })}
    </tr>
  );
}

function StaticRow({
  label,
  labels,
  values,
}: {
  label: string;
  labels: string[];
  values: Array<number | null | undefined>;
}) {
  return (
    <tr>
      <th
        scope="row"
        className="sticky left-0 bg-card py-2 pr-3 text-left text-sm text-muted-foreground"
      >
        {label}
      </th>
      {values.map((v, i) => (
        <td
          key={labels[i]}
          className="px-3 py-2 text-right font-bold tabular-nums text-foreground"
        >
          {formatCurrencyFromCents(v)}
        </td>
      ))}
    </tr>
  );
}

function scenarioSnapshot(
  scenario: ScenarioRenderState,
): PaymentSnapshot | null {
  return scenario.estimate.results[0]?.raw.payment_at_target_price ?? null;
}

function scenarioDownPaymentAmount(
  scenario: ScenarioRenderState,
  snap: PaymentSnapshot | null,
): number | null {
  const explicit = scenario.estimate.request.financing.down_payment_amount;
  if (explicit > 0) {
    return explicit;
  }

  const target = scenario.estimate.request.property.target_price;
  if (typeof target !== "number" || target <= 0) {
    return null;
  }

  if (snap !== null) {
    const derived = target - snap.loan_amount;
    if (derived >= 0) {
      return derived;
    }
  }

  const percent = scenario.estimate.request.financing.down_payment_percent;
  if (typeof percent === "number") {
    return Math.round(target * percent);
  }

  return null;
}
